import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, Plus, FileText, Users, Shield, DollarSign, MapPin, Calendar, LogOut, Menu, X, Briefcase, Send, Eye, CreditCard, CheckCircle2, XCircle, MessageCircle, Star, AlertTriangle, RefreshCw, ScrollText, Navigation, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { TooltipTour, HelpButton, carrierTourSteps, carrierTutorials } from '../components/TutorialSystem';
import PaymentSetup from '../components/PaymentSetup';
import PushNotificationToggle from '../components/PushNotificationToggle';
import AIAssistant from '../components/AIAssistant';

export default function CarrierDashboard() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('jobs');
  const [stats, setStats] = useState({});
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [driverProfiles, setDriverProfiles] = useState([]);
  const [driverJobs, setDriverJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tourComplete, setTourComplete] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [fmcsaStatus, setFmcsaStatus] = useState(null);
  const [verifyingFmcsa, setVerifyingFmcsa] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showLogDetailDialog, setShowLogDetailDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeTrackingJobs, setActiveTrackingJobs] = useState([]);
  const [updatingLocation, setUpdatingLocation] = useState(false);
  
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [selectedJobForQuote, setSelectedJobForQuote] = useState(null);
  const [quoteData, setQuoteData] = useState({ amount: '', estimated_delivery: '', notes: '' });
  
  const [showDriverJobDialog, setShowDriverJobDialog] = useState(false);
  const [newDriverJob, setNewDriverJob] = useState({
    title: '', description: '', license_required: 'CDL-A', salary_range: '', location: '', job_type: 'full-time'
  });
  
  const [showDriverProfileDialog, setShowDriverProfileDialog] = useState(false);
  const [selectedDriverProfile, setSelectedDriverProfile] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    loadDashboard();
    // Get user's location for proximity filtering
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          // Save to backend
          try {
            await apiClient.patch('/users/location', { latitude, longitude, service_radius: 100 });
          } catch (e) {
            console.log('Could not save location');
          }
        },
        (error) => console.log('Location not available:', error),
        { enableHighAccuracy: false, timeout: 10000 }
      );
    }
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, jobsRes, quotesRes, driversRes, driverJobsRes, pendingReviewsRes, fmcsaRes, logsRes, trackingRes] = await Promise.all([
        apiClient.get('/stats/dashboard'),
        apiClient.get('/jobs?status=pending'),
        apiClient.get('/quotes/my-quotes'),
        apiClient.get('/drivers/profiles').catch(() => ({ data: [] })),
        apiClient.get('/drivers/jobs').catch(() => ({ data: [] })),
        apiClient.get('/reviews/pending').catch(() => ({ data: [] })),
        apiClient.get('/fmcsa/status').catch(() => ({ data: null })),
        apiClient.get('/logs/my-activity?limit=20').catch(() => ({ data: { logs: [] } })),
        apiClient.get('/tracking/my-active-jobs').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setAvailableJobs(jobsRes.data);
      setMyQuotes(quotesRes.data);
      setDriverProfiles(driversRes.data);
      setDriverJobs(driverJobsRes.data.filter(j => j.carrier_id === user?.id));
      setPendingReviews(pendingReviewsRes.data);
      setFmcsaStatus(fmcsaRes.data);
      setActivityLogs(logsRes.data.logs || []);
      setActiveTrackingJobs(trackingRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const viewLogDetail = (log) => {
    setSelectedLog(log);
    setShowLogDetailDialog(true);
  };

  const updateJobLocation = async (jobId) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    
    setUpdatingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await apiClient.post(`/tracking/job/${jobId}/location`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            speed: position.coords.speed,
            heading: position.coords.heading,
          });
          toast.success('Location updated');
          loadDashboard();
        } catch (error) {
          toast.error('Failed to update location');
        } finally {
          setUpdatingLocation(false);
        }
      },
      (error) => {
        toast.error('Failed to get location: ' + error.message);
        setUpdatingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const updateTrackingStatus = async (jobId, status) => {
    try {
      await apiClient.post(`/tracking/job/${jobId}/status?status=${status}`);
      toast.success(`Status updated to ${status.replace('_', ' ')}`);
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleVerifyFmcsa = async () => {
    setVerifyingFmcsa(true);
    try {
      const res = await apiClient.post('/fmcsa/verify');
      setFmcsaStatus(res.data);
      toast.success('FMCSA status verified');
      // Refresh user data
      const meRes = await apiClient.get('/auth/me');
      updateUser(meRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify FMCSA status');
    } finally {
      setVerifyingFmcsa(false);
    }
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    
    // Check if carrier can accept jobs
    if (user?.fmcsa_can_accept_jobs === false) {
      toast.error('You cannot submit quotes while your FMCSA status is inactive.');
      return;
    }
    
    try {
      await apiClient.post('/quotes', {
        job_id: selectedJobForQuote.id,
        amount: parseFloat(quoteData.amount),
        estimated_delivery: new Date(quoteData.estimated_delivery).toISOString(),
        notes: quoteData.notes,
      });
      toast.success('Quote submitted successfully!');
      setShowQuoteDialog(false);
      setQuoteData({ amount: '', estimated_delivery: '', notes: '' });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote');
    }
  };

  const handleCreateDriverJob = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/drivers/jobs', newDriverJob);
      toast.success('Driver job posted!');
      setShowDriverJobDialog(false);
      setNewDriverJob({ title: '', description: '', license_required: 'CDL-A', salary_range: '', location: '', job_type: 'full-time' });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create driver job');
    }
  };

  const handleViewDriverProfile = async (driver) => {
    setSelectedDriverProfile(driver);
    setShowDriverProfileDialog(true);
  };

  const handleStartChat = async (customerId) => {
    try {
      await apiClient.post('/chat/start', { participant_id: customerId });
      toast.success('Chat started! Click the chat icon to message.');
    } catch (error) {
      toast.error('Failed to start chat');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewTarget) return;
    
    try {
      await apiClient.post('/customer-reviews', {
        customer_id: reviewTarget.target_id,
        job_id: reviewTarget.job_id,
        rating: reviewData.rating,
        comment: reviewData.comment,
      });
      toast.success('Review submitted successfully!');
      setShowReviewDialog(false);
      setReviewData({ rating: 5, comment: '' });
      setReviewTarget(null);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const openReviewDialog = (review) => {
    setReviewTarget(review);
    setShowReviewDialog(true);
  };

  const handleSubscribe = async () => {
    try {
      const res = await apiClient.post('/subscriptions/create-checkout');
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start payment process');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user?.subscription_status !== 'active') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Complete Your Setup</h2>
            <p className="text-slate-600 mt-2">
              Add a payment method to start submitting quotes. Free to join - you only pay $5 when a job is accepted.
            </p>
          </div>
          <PaymentSetup 
            onSuccess={() => {
              updateUser({ ...user, subscription_status: 'active' });
              navigate('/subscription/success');
            }}
            onCancel={handleLogout}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-orange-500" />
          <span className="font-bold text-slate-900">True Choice Enterprise</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-sidebar-btn">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
        <div className="p-6">
          <div className="flex items-center gap-2 text-white mb-8">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="text-xl font-bold">True Choice</span>
          </div>
          
          <nav className="space-y-2">
            <button onClick={() => setActiveTab('jobs')} className={`sidebar-link w-full ${activeTab === 'jobs' ? 'sidebar-link-active' : ''}`} data-testid="nav-jobs">
              <FileText className="h-5 w-5" /> Browse Jobs
            </button>
            <button onClick={() => setActiveTab('quotes')} className={`sidebar-link w-full ${activeTab === 'quotes' ? 'sidebar-link-active' : ''}`} data-testid="nav-quotes">
              <Send className="h-5 w-5" /> My Quotes
            </button>
            <button onClick={() => setActiveTab('tracking')} className={`sidebar-link w-full ${activeTab === 'tracking' ? 'sidebar-link-active' : ''}`} data-testid="nav-tracking">
              <Navigation className="h-5 w-5" /> GPS Tracking
            </button>
            <button onClick={() => setActiveTab('activity')} className={`sidebar-link w-full ${activeTab === 'activity' ? 'sidebar-link-active' : ''}`} data-testid="nav-activity">
              <ScrollText className="h-5 w-5" /> Activity Log
            </button>
            <button onClick={() => setActiveTab('drivers')} className={`sidebar-link w-full ${activeTab === 'drivers' ? 'sidebar-link-active' : ''}`} data-testid="nav-drivers">
              <Users className="h-5 w-5" /> Find Drivers
            </button>
            <button onClick={() => setActiveTab('hiring')} className={`sidebar-link w-full ${activeTab === 'hiring' ? 'sidebar-link-active' : ''}`} data-testid="nav-hiring">
              <Briefcase className="h-5 w-5" /> Driver Jobs
            </button>
          </nav>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-800">
          <div className="text-slate-400 text-sm mb-2">{user?.company_name || user?.email}</div>
          <button onClick={handleLogout} className="sidebar-link w-full text-red-400 hover:text-red-300" data-testid="logout-btn">
            <LogOut className="h-5 w-5" /> Log Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {user?.company_name || `Welcome, ${user?.first_name}!`}
              </h1>
              <p className="text-slate-600">Carrier Dashboard</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <PushNotificationToggle 
                token={localStorage.getItem('token')} 
                onStatusChange={(status) => {
                  if (status) {
                    toast.success('Push notifications enabled! You\'ll be notified of new jobs near you.');
                  }
                }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="stat-card">
              <div className="stat-value">{stats.quotes_sent || 0}</div>
              <div className="stat-label">Quotes Sent</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.quotes_accepted || 0}</div>
              <div className="stat-label">Quotes Accepted</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.driver_jobs || 0}</div>
              <div className="stat-label">Driver Jobs Posted</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.driver_jobs || 0}</div>
              <div className="stat-label">Driver Job Posts</div>
            </div>
          </div>

          {/* FMCSA Compliance Warning Banner */}
          {user?.fmcsa_warning_message && (
            <div className={`rounded-xl border p-6 mb-8 ${
              user?.fmcsa_can_accept_jobs === false 
                ? 'bg-red-50 border-red-300' 
                : 'bg-amber-50 border-amber-300'
            }`} data-testid="fmcsa-warning-banner">
              <div className="flex items-start gap-4">
                <AlertTriangle className={`h-6 w-6 mt-0.5 ${
                  user?.fmcsa_can_accept_jobs === false ? 'text-red-500' : 'text-amber-500'
                }`} />
                <div className="flex-1">
                  <h2 className={`text-lg font-bold ${
                    user?.fmcsa_can_accept_jobs === false ? 'text-red-900' : 'text-amber-900'
                  }`}>
                    {user?.fmcsa_can_accept_jobs === false ? 'FMCSA Status: Restricted' : 'FMCSA Compliance Warning'}
                  </h2>
                  <p className={`mt-1 ${
                    user?.fmcsa_can_accept_jobs === false ? 'text-red-700' : 'text-amber-700'
                  }`}>
                    {user?.fmcsa_warning_message}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button 
                      onClick={handleVerifyFmcsa}
                      disabled={verifyingFmcsa}
                      variant="outline"
                      className="border-current"
                      data-testid="verify-fmcsa-btn"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${verifyingFmcsa ? 'animate-spin' : ''}`} />
                      {verifyingFmcsa ? 'Verifying...' : 'Re-verify Status'}
                    </Button>
                    <a 
                      href="https://www.fmcsa.dot.gov/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium underline"
                    >
                      Visit FMCSA.gov
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FMCSA Status Card (always show for carriers) */}
          {fmcsaStatus?.has_record && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8" data-testid="fmcsa-status-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Shield className={`h-6 w-6 ${
                    fmcsaStatus?.fmcsa_status === 'ACTIVE' ? 'text-green-500' : 'text-red-500'
                  }`} />
                  <h2 className="text-lg font-bold text-slate-900">FMCSA Status</h2>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  fmcsaStatus?.fmcsa_status === 'ACTIVE' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {fmcsaStatus?.fmcsa_status || 'Unknown'}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">DOT Number</p>
                  <p className="font-medium text-slate-900">{fmcsaStatus?.dot_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">MC Number</p>
                  <p className="font-medium text-slate-900">{fmcsaStatus?.mc_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Legal Name</p>
                  <p className="font-medium text-slate-900">{fmcsaStatus?.legal_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Last Verified</p>
                  <p className="font-medium text-slate-900">
                    {fmcsaStatus?.last_verified 
                      ? format(new Date(fmcsaStatus.last_verified), 'MMM d, yyyy')
                      : 'Never'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pending Reviews Section */}
          {pendingReviews.length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Star className="h-6 w-6 text-amber-500" />
                <h2 className="text-lg font-bold text-slate-900">Rate Your Customers</h2>
              </div>
              <p className="text-slate-600 mb-4">Help other carriers by sharing your experience with these customers.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingReviews.map((review) => (
                  <div key={review.job_id} className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                    <h3 className="font-semibold text-slate-900">{review.job_title}</h3>
                    <p className="text-sm text-slate-500 mt-1">Customer: {review.target_name}</p>
                    <Button
                      onClick={() => openReviewDialog(review)}
                      className="btn-accent mt-3 w-full"
                      data-testid={`review-btn-${review.job_id}`}
                    >
                      <Star className="h-4 w-4 mr-2" /> Leave Review
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content based on active tab */}
          {activeTab === 'jobs' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Available Jobs</h2>
              </div>
              {availableJobs.length === 0 ? (
                <div className="empty-state py-16">
                  <FileText className="empty-state-icon" />
                  <p className="empty-state-text">No available jobs at the moment</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {availableJobs.map((job) => (
                    <div key={job.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{job.title}</h3>
                          <p className="text-sm text-slate-600 mt-1">{job.description}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-2">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {job.pickup_city}, {job.pickup_state} → {job.delivery_city}, {job.delivery_state}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {format(new Date(job.pickup_date), 'MMM d, yyyy')}
                            </span>
                            <span className="badge bg-slate-100 text-slate-700">
                              {job.job_type}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => handleStartChat(job.customer_id)}
                            data-testid={`chat-customer-${job.customer_id}`}
                          >
                            <MessageCircle className="h-4 w-4 mr-2" /> Chat
                          </Button>
                          <Button
                            onClick={() => { setSelectedJobForQuote(job); setShowQuoteDialog(true); }}
                            className="btn-accent"
                            data-testid={`quote-job-${job.id}`}
                          >
                            <Send className="h-4 w-4 mr-2" /> Send Quote
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'quotes' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">My Quotes</h2>
              </div>
              {myQuotes.length === 0 ? (
                <div className="empty-state py-16">
                  <Send className="empty-state-icon" />
                  <p className="empty-state-text">No quotes submitted yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {myQuotes.map((quote) => (
                    <div key={quote.id} className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-2xl font-bold text-orange-500">${quote.amount.toFixed(2)}</div>
                          <div className="text-sm text-slate-500 mt-1">
                            Est. delivery: {format(new Date(quote.estimated_delivery), 'MMM d, yyyy')}
                          </div>
                        </div>
                        <span className={`badge ${quote.status === 'accepted' ? 'badge-active' : quote.status === 'rejected' ? 'badge-cancelled' : 'badge-pending'}`}>
                          {quote.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'drivers' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Available Drivers</h2>
              </div>
              {driverProfiles.length === 0 ? (
                <div className="empty-state py-16">
                  <Users className="empty-state-icon" />
                  <p className="empty-state-text">No drivers available</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {driverProfiles.map((driver) => (
                    <div key={driver.id} className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-slate-900">{driver.first_name} {driver.last_name}</h3>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className={`license-badge ${driver.license_type.includes('CDL') ? 'license-badge-cdl' : ''}`}>
                              {driver.license_type}
                            </span>
                            <span className="text-sm text-slate-500">{driver.years_experience} years exp.</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => handleViewDriverProfile(driver)} data-testid={`view-driver-${driver.id}`}>
                            <Eye className="h-4 w-4 mr-2" /> View Profile
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'hiring' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">My Driver Job Posts</h2>
                <Button onClick={() => setShowDriverJobDialog(true)} className="btn-accent" data-testid="post-driver-job-btn">
                  <Plus className="h-4 w-4 mr-2" /> Post Driver Job
                </Button>
              </div>
              {driverJobs.length === 0 ? (
                <div className="empty-state py-16">
                  <Briefcase className="empty-state-icon" />
                  <p className="empty-state-text">No driver jobs posted</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {driverJobs.map((job) => (
                    <div key={job.id} className="p-6">
                      <h3 className="font-semibold text-slate-900">{job.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{job.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-2">
                        <span className="badge bg-slate-100 text-slate-700">{job.license_required}</span>
                        <span>{job.salary_range}</span>
                        <span>{job.location}</span>
                        <span>{job.applicant_count} applicants</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* GPS Tracking Tab */}
          {activeTab === 'tracking' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200" data-testid="tracking-section">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">GPS Tracking</h2>
                <p className="text-slate-500 mt-1">Update your location for active jobs</p>
              </div>
              
              {activeTrackingJobs.length === 0 ? (
                <div className="p-12 text-center">
                  <Navigation className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No active jobs to track</p>
                  <p className="text-sm text-slate-400 mt-1">Accept a quote to start tracking</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {activeTrackingJobs.map((job) => (
                    <div key={job.job_id} className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{job.job_title}</h3>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              job.tracking_status === 'delivered' ? 'bg-green-100 text-green-700' :
                              job.tracking_status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                              job.tracking_status === 'picked_up' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {job.tracking_status.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className="text-sm text-slate-500">
                              {job.pickup_city}, {job.pickup_state} → {job.delivery_city}, {job.delivery_state}
                            </span>
                          </div>
                          
                          {job.current_location && (
                            <p className="text-sm text-slate-500 mt-2">
                              <MapPin className="h-4 w-4 inline mr-1" />
                              Last update: {format(new Date(job.current_location.timestamp), 'MMM d, h:mm a')}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => updateJobLocation(job.job_id)}
                            disabled={updatingLocation}
                            className="btn-accent"
                            data-testid={`update-location-${job.job_id}`}
                          >
                            <MapPin className="h-4 w-4 mr-2" />
                            {updatingLocation ? 'Updating...' : 'Update Location'}
                          </Button>
                          
                          <Select 
                            value={job.tracking_status} 
                            onValueChange={(v) => updateTrackingStatus(job.job_id, v)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Update Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="picked_up">Picked Up</SelectItem>
                              <SelectItem value="in_transit">In Transit</SelectItem>
                              <SelectItem value="near_delivery">Near Delivery</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activity Logs Tab */}
          {activeTab === 'activity' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="activity-log-section">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Activity Log</h2>
                <p className="text-slate-500 mt-1">Your job history with BOL and contract details</p>
              </div>
              
              {activityLogs.length === 0 ? (
                <div className="p-12 text-center">
                  <ScrollText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No activity logged yet</p>
                  <p className="text-sm text-slate-400 mt-1">Your accepted jobs will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.log_type === 'quote_accepted' ? 'bg-green-100 text-green-700' :
                              log.log_type === 'job_completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {log.log_type.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className="text-sm text-slate-500">
                              {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          <h3 className="font-semibold text-slate-900">{log.job_title}</h3>
                          <p className="text-slate-600 text-sm mt-1">{log.description}</p>
                          
                          {log.bol_number && (
                            <div className="mt-3 flex items-center gap-4 text-sm">
                              <span className="text-slate-500">
                                <FileText className="h-4 w-4 inline mr-1" />
                                BOL: <span className="font-mono font-medium text-slate-700">{log.bol_number}</span>
                              </span>
                              {log.amount && (
                                <span className="text-slate-500">
                                  <DollarSign className="h-4 w-4 inline mr-1" />
                                  Amount: <span className="font-medium text-green-600">${log.amount.toFixed(2)}</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewLogDetail(log)}
                          data-testid={`view-log-${log.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" /> View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Quote Dialog */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Quote</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitQuote} className="space-y-4 mt-4">
            <div>
              <Label>Quote Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={quoteData.amount}
                onChange={(e) => setQuoteData({ ...quoteData, amount: e.target.value })}
                required
                data-testid="quote-amount-input"
              />
            </div>
            <div>
              <Label>Estimated Delivery Date</Label>
              <Input
                type="datetime-local"
                value={quoteData.estimated_delivery}
                onChange={(e) => setQuoteData({ ...quoteData, estimated_delivery: e.target.value })}
                required
                data-testid="quote-delivery-input"
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={quoteData.notes}
                onChange={(e) => setQuoteData({ ...quoteData, notes: e.target.value })}
                rows={3}
              />
            </div>
            <Button type="submit" className="btn-accent w-full" data-testid="submit-quote-btn">
              Submit Quote
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Driver Job Dialog */}
      <Dialog open={showDriverJobDialog} onOpenChange={setShowDriverJobDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Post Driver Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDriverJob} className="space-y-4 mt-4">
            <div>
              <Label>Job Title</Label>
              <Input
                value={newDriverJob.title}
                onChange={(e) => setNewDriverJob({ ...newDriverJob, title: e.target.value })}
                required
                data-testid="driver-job-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newDriverJob.description}
                onChange={(e) => setNewDriverJob({ ...newDriverJob, description: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>License Required</Label>
              <Select value={newDriverJob.license_required} onValueChange={(v) => setNewDriverJob({ ...newDriverJob, license_required: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CDL-A">CDL-A</SelectItem>
                  <SelectItem value="CDL-B">CDL-B</SelectItem>
                  <SelectItem value="CDL-C">CDL-C</SelectItem>
                  <SelectItem value="Non-CDL">Non-CDL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Salary Range</Label>
              <Input
                value={newDriverJob.salary_range}
                onChange={(e) => setNewDriverJob({ ...newDriverJob, salary_range: e.target.value })}
                placeholder="e.g., $50,000 - $70,000/year"
                required
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={newDriverJob.location}
                onChange={(e) => setNewDriverJob({ ...newDriverJob, location: e.target.value })}
                placeholder="e.g., Dallas, TX"
                required
              />
            </div>
            <Button type="submit" className="btn-accent w-full" data-testid="submit-driver-job-btn">
              Post Job
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Driver Profile Dialog */}
      <Dialog open={showDriverProfileDialog} onOpenChange={setShowDriverProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Driver Profile</DialogTitle>
          </DialogHeader>
          {selectedDriverProfile && (
            <div className="mt-4 space-y-6">
              {/* Basic Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                  <Users className="h-8 w-8 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {selectedDriverProfile.first_name} {selectedDriverProfile.last_name}
                  </h3>
                  <p className="text-slate-600">{selectedDriverProfile.email}</p>
                  <p className="text-slate-500">{selectedDriverProfile.phone}</p>
                </div>
              </div>

              {/* License Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-3">License Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">License Type</span>
                    <p className={`font-semibold ${selectedDriverProfile.license_type?.includes('CDL') ? 'text-green-600' : ''}`}>
                      {selectedDriverProfile.license_type}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">License Number</span>
                    <p className="font-mono font-semibold">{selectedDriverProfile.license_number}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">State</span>
                    <p className="font-semibold">{selectedDriverProfile.license_state}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Expiry</span>
                    <p className="font-semibold">
                      {selectedDriverProfile.license_expiry && format(new Date(selectedDriverProfile.license_expiry), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Experience</span>
                    <p className="font-semibold">{selectedDriverProfile.years_experience} years</p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedDriverProfile.bio && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">About</h4>
                  <p className="text-slate-600">{selectedDriverProfile.bio}</p>
                </div>
              )}

              {/* Certifications */}
              {selectedDriverProfile.certifications?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDriverProfile.certifications.map((cert, i) => (
                      <span key={i} className="badge bg-blue-100 text-blue-700">{cert}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tooltip Tour for first-time users */}
      {!loading && (
        <TooltipTour 
          steps={carrierTourSteps} 
          storageKey="carrier_tour_complete"
          onComplete={() => setTourComplete(true)}
        />
      )}

      {/* Help Button with Video Tutorials */}
      <HelpButton tutorials={carrierTutorials} />

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Rate Your Experience
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitReview} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700">Job: {reviewTarget?.job_title}</Label>
              <p className="text-sm text-slate-500">Customer: {reviewTarget?.target_name}</p>
            </div>
            
            <div>
              <Label>Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                    className={`p-2 rounded-lg transition-all ${
                      star <= reviewData.rating 
                        ? 'text-amber-400 bg-amber-50 hover:bg-amber-100' 
                        : 'text-slate-300 bg-slate-50 hover:bg-slate-100'
                    }`}
                    data-testid={`star-${star}`}
                  >
                    <Star className={`h-8 w-8 ${star <= reviewData.rating ? 'fill-amber-400' : ''}`} />
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {reviewData.rating === 1 && 'Poor'}
                {reviewData.rating === 2 && 'Fair'}
                {reviewData.rating === 3 && 'Good'}
                {reviewData.rating === 4 && 'Very Good'}
                {reviewData.rating === 5 && 'Excellent'}
              </p>
            </div>
            
            <div>
              <Label>Comment (Optional)</Label>
              <Textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                placeholder="Share your experience with this customer..."
                rows={4}
                data-testid="review-comment"
              />
            </div>
            
            <Button type="submit" className="btn-accent w-full" data-testid="submit-review-btn">
              Submit Review
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Log Detail Dialog */}
      <Dialog open={showLogDetailDialog} onOpenChange={setShowLogDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-orange-500" />
              Activity Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="mt-4 space-y-6">
              {/* Job Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-3">Job Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Job Title</span>
                    <p className="font-semibold">{selectedLog.job_title}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Type</span>
                    <p className={`font-semibold px-2 py-0.5 rounded-full text-xs inline-block ${
                      selectedLog.log_type === 'quote_accepted' ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {selectedLog.log_type.replace('_', ' ').toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Date</span>
                    <p className="font-semibold">{format(new Date(selectedLog.created_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Amount</span>
                    <p className="font-semibold text-green-600">${selectedLog.amount?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>

              {/* BOL Info */}
              {selectedLog.bol_number && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Bill of Lading
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700">BOL Number</span>
                      <p className="font-mono font-bold text-blue-900">{selectedLog.bol_number}</p>
                    </div>
                    {selectedLog.bol_data && (
                      <>
                        <div>
                          <span className="text-blue-700">Ship Date</span>
                          <p className="font-semibold text-blue-900">{selectedLog.bol_data.ship_date || 'N/A'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Contract Info */}
              {selectedLog.contract_data && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Contractual Agreement
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-amber-700">Agreement Type</span>
                      <p className="font-semibold text-amber-900">{selectedLog.contract_data.agreement_type}</p>
                    </div>
                    {selectedLog.contract_data.parties && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-amber-700">Customer</span>
                          <p className="font-semibold text-amber-900">{selectedLog.contract_data.parties.customer?.name}</p>
                        </div>
                        <div>
                          <span className="text-amber-700">Carrier</span>
                          <p className="font-semibold text-amber-900">{selectedLog.contract_data.parties.carrier?.name}</p>
                        </div>
                      </div>
                    )}
                    {selectedLog.contract_data.financial_terms && (
                      <div className="mt-3 pt-3 border-t border-amber-200">
                        <span className="text-amber-700 font-medium">Financial Terms</span>
                        <p className="text-amber-800 mt-1">
                          Quoted Amount: <strong>${selectedLog.contract_data.financial_terms.quoted_amount?.toFixed(2) || '0.00'}</strong>
                        </p>
                        {selectedLog.contract_data.financial_terms.carrier_transaction_fee && (
                          <p className="text-amber-800">
                            Transaction Fee: ${selectedLog.contract_data.financial_terms.carrier_transaction_fee.toFixed(2)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Description</h4>
                <p className="text-slate-600">{selectedLog.description}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Assistant */}
      <AIAssistant 
        token={localStorage.getItem('token')} 
        userRole="carrier"
        onQuoteSuggestion={(suggestion) => {
          toast.success('Quote suggestion ready! Check the AI Assistant.');
        }}
      />
    </div>
  );
}
